import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, set, useForm, useFormContext, } from "react-hook-form";
import { Skeleton, Slider } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Select, selectCustomStyles } from "./common/Select";
import { fetchLanguages } from "slices/languageSlice";
import { SubmitButton } from "./common/buttons/SubmitButton";
import { setError } from "slices/scheduleSlice";
import { AsyncPaginate } from "react-select-async-paginate";
import { fetchInterpreters } from "slices/interpreterSlice";
import { interpreterName } from "helpers/interpreterName";
import { updateAppointment } from "slices/appointmentSlice";
import { addAppointment } from "slices/appointmentSlice";
import { useNavigate } from "react-router-dom";
import { appointmentsSelector } from "slices/appointmentSlice";
import { fetchAppointmentView } from "slices/appointmentSlice";
import { commentsTypeOptions, encounterTypeOptions, } from "./appointment/OptionsArray";
import { fetchJobLocations } from "slices/jobLocationSlice";
import { jobLocationSelector } from "slices/jobLocationSlice";
import { fetchJobLocationById } from "slices/jobLocationSlice";
import { CustomSwitch } from "./common/Switch";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { interpreterSelector } from "slices/interpreterSlice";
import ErrorMessage from "./common/ErrorMessage";
import moment from "moment";
import Checkbox from "./common/Checkbox";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import TagInput from "./TagInput";
import { userSelector } from "slices/userSlice";
import { setJobLocationById } from "slices/jobLocationSlice";
import { usePermissions } from "layouts/Admin";

// Business hours: Mon–Fri, 8:00am (480 min) to 4:30pm (990 min).
// Returns true when the selected date/time falls outside that window.
// Returns false (no warning) until both date and time are valid.
const BUSINESS_START_MINUTES = 8 * 60; // 08:00
const BUSINESS_END_MINUTES = 16 * 60 + 30; // 16:30
const isOutsideBusinessHours = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return false;
  const date = moment(dateStr, "YYYY-MM-DD", true);
  const time = moment(timeStr, ["HH:mm", "h:mm A"], true);
  if (!date.isValid() || !time.isValid()) return false;

  const day = date.day(); // 0 = Sunday, 6 = Saturday
  if (day === 0 || day === 6) return true;

  const minutes = time.hours() * 60 + time.minutes();
  return minutes < BUSINESS_START_MINUTES || minutes > BUSINESS_END_MINUTES;
};

const ScheduleForm = ({
  handleClose,
  item,
  callBack,
  isDuplicate = false,
  itsBootstrap = true,
  isNew = false,
  append = false,
  values,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, logout } = useSelector(userSelector);
  const defaultDuration = 120;

  const methods = useForm({
    defaultValues: {
      duration: defaultDuration,
      date: moment(new Date()).format("YYYY-MM-DD"),
      start_time: moment(new Date()),
      comments: "Other",
      simultaneous_interpretation: 1,
    },
  });

  const {
    setFocus,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    setValue,
    setError: setErrors,
    clearErrors,
    getValues,
  } = methods;

  const { error, appointments: scheduleLists } = useSelector(appointmentsSelector);
  const [loaderState, setLoaderState] = React.useState(false);
  const [nonCertifiedInterpreter, setNonCertifiedInterpreter] = React.useState(0);
  const [certifiedInterpreter, setCertifiedInterpreter] = React.useState(1);
  const [excludedInterpreters, setExcludedInterpreters] = React.useState([]);

  // Edit vs create, and whether the job is still open (accepting invites)
  const isEdit = !!item?.id && !isDuplicate;
  const isJobOpen = item?.status === 'open';
  // Show the interpreter pool/exclusion controls only when creating, or editing an OPEN job
  const showInterpreterSection = !isEdit || isJobOpen;
  // Per-checkbox permissions (admins/super_admins always pass)
  const { checkPermission } = usePermissions();
  const canOpenNonCertified = checkPermission('Open Job Non-Certified Interpreters');
  const canOpenCertified = checkPermission('Open Job Certified Interpreters');


  //#region  CLEAR UP ALL FIELDS
  const clearAllFields = React.useCallback(() => {
    setValue("language_id", "");
    setValue("interpreter_id", "");
    setfilterLanguages(null);
    setValue("date", moment(new Date()).format("YYYY-MM-DD"));
    setValue("start_time", moment().format("HH:mm"));
    setValue("job_location_id", "");
    setValue("location_address", "");
    setValue("location_notes", "");
    setValue("duration", defaultDuration);
    setValue("job_notes_internal", "");
    setValue("job_notes_external", "");
    setCertifiedInterpreter(1);
    setNonCertifiedInterpreter(0);
    setValue("simultaneous_interpretation", 1);
    setValue("requester_name", user?.user?.name);
    setValue("requester_email", [user?.user?.email]);
    setValue("requester_phone", "");
    setExcludedInterpreters([]);
    clearErrors();
    dispatch(setError({}));
    dispatch(setJobLocationById({}));
  }, []);
  //#endregion

  useEffect(() => {
    return () => {
      clearAllFields();
    }
  }, [item])

  useEffect(() => {
    setValue("requester_name", user?.user?.name);
    setValue("requester_email", [user?.user?.email]);
  }, [user?.user])

  //#region useEffects
  React.useEffect(() => {
    setValue("language_id", item?.language_id || "");
    setValue("date", item?.date || moment(new Date()).format("YYYY-MM-DD"));
    setValue("start_time", item?.start_time || moment().format("HH:mm"));

    setValue("duration", item?.duration || defaultDuration);
    setValue("location_address", item?.appointment_details?.location_address || "");
    setValue("location_notes", item?.appointment_details?.location_notes || "");
    setValue("job_notes_internal", item?.appointment_details?.job_notes_internal || "");
    setValue("job_notes_external", item?.appointment_details?.job_notes_external || "");
    // Default ON for new jobs; edits/duplicates keep the job's saved value
    setValue("simultaneous_interpretation", item ? (item.simultaneous_interpretation || 0) : 1);
    setValue("requester_name", item?.appointment_details?.requester_name || user?.user?.name);
    setValue(
      "requester_phone",
      item?.appointment_details?.requester_phone || ""
    );
    setValue(
      "requester_email",
      item?.appointment_details?.requester_email || [user?.user?.email]
    );

    // On edit, reflect the job's saved certification setting in the checkboxes
    if (item?.id && !isDuplicate) {
      const oj = item?.openJob;
      if (oj === 'all') { setCertifiedInterpreter(1); setNonCertifiedInterpreter(1); }
      else if (oj === 'certified') { setCertifiedInterpreter(1); setNonCertifiedInterpreter(0); }
      else if (oj === 'nonCertified') { setCertifiedInterpreter(0); setNonCertifiedInterpreter(1); }
      else if (oj === 'none') { setCertifiedInterpreter(0); setNonCertifiedInterpreter(0); }
    }
  }, [item?.appid]);
  //#endregion

  //#region SUBMIT FORM
  const submitCallback = (status, message, data) => {
    if (status) {
      toast.success(message, {
        className: "successToast",
      });
      clearAllFields();
      handleClose();
      setLoaderState(false);
      Swal.fire({
        title: "Success!",
        text: message,
        icon: "success",
      }).then(() => {
        dispatch(fetchAppointmentView(data?.id));
        navigate(`/appointments/view/${data?.id}`);
      });
    } else {
      toast.error(message, {
        className: "errorToast",
      });
      setTimeout(() => {
        setLoaderState(false);
      }, 1000);
    }
  };

  const onSubmit = React.useCallback(
    (data) => {
      dispatch(setError({}));
      setLoaderState(true);

      // requester_email to be always an array
      if (typeof data.requester_email === "string") {
        try {
          data.requester_email = JSON.parse(data.requester_email);
          if (!Array.isArray(data.requester_email)) {
            data.requester_email = [];
          }
        } catch {
          data.requester_email = data.requester_email
            ? data.requester_email.split(",").map((email) => email.trim())
            : [];
        }
      }

      // Your rest of validations and submission
      if (data?.interpreter_id) {
        if (data?.checkin_date && !data?.checkin_time) {
          setErrors("checkin_time", { message: "Please select checkin time" });
          return;
        }
        if (data?.checkout_date && !data?.checkout_time) {
          setErrors("checkout_time", {
            message: "Please select checkout time",
          });
          return;
        }
      }

      let openJobFilter = 'all';
      if (certifiedInterpreter == 1 && nonCertifiedInterpreter != 1) {
        openJobFilter = "certified";
      } else if (nonCertifiedInterpreter == 1 && certifiedInterpreter != 1) {
        openJobFilter = "nonCertified";
      }

      const formData = {
        ...data,
        openJob: openJobFilter,
        excludedInterpreters: excludedInterpreters,
      }

      if (item?.id && !isDuplicate) {
        dispatch(updateAppointment(formData, item?.id, submitCallback, true));
      } else {
        dispatch(addAppointment(formData, submitCallback, true));
      }
    },
    [item, callBack, append, isNew, isDuplicate, excludedInterpreters, certifiedInterpreter, nonCertifiedInterpreter, values, watch("date")]
  );

  const scrollToError = (fieldName) => {
    const element = document.querySelector(`[name="${fieldName}"]`);
    if (element) {
      const parentContainer = document.querySelector("#scheduleEncounter"); // Replace with your container class
      if (parentContainer) {
        const offsetTop = element.offsetTop - parentContainer.offsetTop;
        parentContainer.scrollTo({
          top: offsetTop,
          behavior: "smooth",
        });
      }
      setFocus(fieldName);
    }
  };

  const onError = (error) => {
    const firstErrorField = Object.keys(error)[0];
    if (firstErrorField) scrollToError(firstErrorField);
  };
  //#endregion

  // #region language dropdown
  const [filterLanguages, setfilterLanguages] = useState({
    label: "",
    value: "",
  });

  useEffect(() => {
    if (item?.language?.name && item?.language?.id) {
      setfilterLanguages({
        label: item?.language?.name || "",
        value: item?.language?.id || "",
      });
    } else {
      setValue("language_id", "");
      setfilterLanguages(null);
    }
  }, [item?.language, setValue]);

  const filteredLanguageLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    console.log("inputValue", inputValue);
    const result = await dispatch(
      fetchLanguages(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
          status: "active",
        },
        true,
        true
      )
    );
    return {
      options: result.data.map((val) => ({
        value: val.id,
        label: val.name,
      })),

      hasMore: page !== result?.last_page,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  return (
    <FormProvider {...methods}>
      <form className="encounter_form">
        {/* INPUTS */}
        <>
          {/* LANGUAGE SELECT */}
          <div className="row-primary">
            <h2>Language *</h2>
            <div className="w-100">
              <Controller
                name="language_id"
                control={control}
                rules={{
                  required: "Please select a language",
                }}
                render={({ field }) => (
                  <div style={{ width: "100%" }}>
                    <AsyncPaginate
                      menuPlacement="auto"
                      debounceTimeout={500}
                      styles={{
                        ...selectCustomStyles,
                      }}
                      value={filterLanguages}
                      loadOptions={filteredLanguageLoadOptions}
                      // The server already matched the typed text — don't let react-select
                      // filter the results again (that hid legitimate matches)
                      filterOption={null}
                      isClearable
                      escapeClearsValue
                      onChange={(obj) => {
                        if (obj?.value) {
                          setfilterLanguages(obj);
                          field.onChange(obj.value);
                        } else {
                          setfilterLanguages(null);
                          field.onChange(null);
                        }
                      }}
                      additional={{
                        page: item?.state?.id ? 2 : 1,
                      }}
                    />
                    {errors?.language_id?.message && (
                      <div style={{ marginBottom: 10 }}>
                        <ErrorMessage text={errors?.language_id?.message} />
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          {/* DATE TIME INPUT — hidden on edit; time changes go through Reschedule */}
          {!isEdit && <DateTimeInputs isDuplicate={isDuplicate} isNew={isNew} />}

          {/* JOB LOCATION WITh COURTROOM */}
          <JobLocationAndCoutrooms item={item} dispatch={dispatch} control={control} errors={errors} watch={watch} setValue={setValue} />

          {/* DURATION */}
          <DurationRange control={control} defaultDuration={defaultDuration} />

          {/* JOB NOTES INTERNAL */}
          <div className="row-primary">
            <h2>Job Notes (Internal)</h2>
            <Controller
              name="job_notes_internal"
              control={control}
              render={({ field }) => (
                <textarea
                  className="type_inp input-in"
                  {...field}
                  placeholder="Job notes"
                ></textarea>
              )}
            />
          </div>

          {/* JOB NOTES EXTERNAL — hidden per client request (use Location Notes instead).
              Field value is still preserved/submitted via setValue defaults. */}
          {/* <div className="row-primary">
            <h2>Job Notes (External)</h2>
            <Controller
              name="job_notes_external"
              control={control}
              render={({ field }) => (
                <textarea
                  className="type_inp input-in"
                  {...field}
                  placeholder="Job notes"
                ></textarea>
              )}
            />
          </div> */}

          {showInterpreterSection && (
          <>
          {/* OPEN NON CERTIFIED INTERPRETER — permission-gated for subusers */}
          {canOpenNonCertified && (
          <div className="row-primary align-items-center encounter_switch">
            <label className="custom-checkbox checkbox">
              <input
                type="checkbox"
                name="none_certified_interpreter"
                id="none_cert_int"
                checked={nonCertifiedInterpreter === 1 ? true : false}
                onChange={(e) => setNonCertifiedInterpreter(e.target.checked ? 1 : 0)}
              />
              <span className="checkmark"></span>
            </label>
            <label htmlFor="none_cert_int" className="labelTitle m-0">Open job to Non-Certified Interpreters</label>
          </div>
          )}

          {/* OPEN CERTIFIED INTERPRETER — permission-gated for subusers */}
          {canOpenCertified && (
          <div className="row-primary align-items-center encounter_switch">
            <label className="custom-checkbox checkbox">
              <input
                type="checkbox"
                name="certified_interpreter"
                id="cert_int"
                checked={certifiedInterpreter === 1 ? true : false}
                onChange={(e) => setCertifiedInterpreter(e.target.checked ? 1 : 0)}
              />
              <span className="checkmark"></span>
            </label>
            <label htmlFor="cert_int" className="labelTitle m-0">Open job for Certified Interpreters</label>
          </div>
          )}
          </>
          )}

          <div className="row-primary flex-row align-items-center encounter_switch" style={{ alignItems: "center" }}>
            <h2 className='mt-0'>Simultaneous Interpretation</h2>
            <Controller
              name="simultaneous_interpretation"
              control={control}
              required
              render={({ field }) => (
                <CustomSwitch
                  labelText="active"
                  checked={field.value == 1 ? true : false}
                  handleChange={(checked) => field.onChange(checked ? 1 : 0)}

                />
              )}
            />
          </div>

          {/* REQUESTER */}
          <div className="row-primary">
            <h2>Requestor</h2>
            <div className="w-100">
              <Controller
                name="requester_name"
                control={control}
                render={({ field }) => (
                  <input className="input-in type_inp" type="text" readOnly {...field} />
                )}
              />
            </div>
          </div>

          {/* REQUESTOR EMAIL */}
          <div className="row-primary">
            <h2>Requestor Email(s)</h2>
            <div className="w-100">
              <Controller
                name="requester_email"
                control={control}
                rules={{
                  validate: (emails, formValues) => {
                    // agar Encounter Type = "In Person" ho to required nahi hoga
                    if (formValues.type === "In Person") {
                      return true;
                    }

                    // normalize emails into array
                    let emailsArray = [];
                    if (typeof emails === "string") {
                      emailsArray = emails
                        .split(",")
                        .map((e) => e.trim())
                        .filter((e) => e.length > 0);
                    } else if (Array.isArray(emails)) {
                      emailsArray = emails;
                    }

                    // email regex
                    const emailPattern =
                      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

                    // invalid check
                    const invalidEmails = emailsArray.filter(
                      (email) => !emailPattern.test(email)
                    );
                    if (invalidEmails.length > 0) {
                      return `Invalid email(s): ${invalidEmails.join(", ")}`;
                    }

                    return true;
                  },
                }}
                render={({ field }) => {
                  let emailsArray = [];

                  if (typeof field.value === "string") {
                    emailsArray = field.value
                      .split(",")
                      .map((email) => email.trim())
                      .filter((email) => email.length > 0);
                  } else if (Array.isArray(field.value)) {
                    emailsArray = field.value;
                  }

                  return (
                    <div>
                      <TagInput
                        value={emailsArray}
                        onChange={(newTags) => {
                          const newEmailsString = newTags.join(", ");
                          field.onChange(newEmailsString);
                        }}
                      />
                    </div>
                  );
                }}
              />

              {errors?.requester_email?.message && (
                <div style={{ marginBottom: 10 }}>
                  <ErrorMessage text={errors?.requester_email?.message} />
                </div>
              )}
            </div>
          </div>

          {/* REQUESTOR PHONE NUMBER — hidden per client request (unused field).
              Field value is still preserved/submitted via setValue defaults. */}
          {/* <div className="row-primary">
            <h2>Requestor Phone Number</h2>
            <div className="w-100">
              <Controller
                name="requester_phone"
                control={control}
                render={({ field }) => (
                  <input
                    className="input-in type_inp"
                    type="tel"
                    name="requester_phone"
                    placeholder="Phone Number"
                    {...field}
                    onChange={(e) => {
                      if (e.target.value.length <= 20) {
                        field.onChange(e);
                      }
                    }}
                  />
                )}
              />

              {error?.errors?.phone?.map((error) => (
                <div style={{ marginBottom: 10 }}>
                  <ErrorMessage text={error} />
                </div>
              ))}
            </div>
          </div> */}

          {showInterpreterSection && (
            <InterpreterExclusion
              watch={watch}
              dispatch={dispatch}
              certified={certifiedInterpreter}
              nonCertified={nonCertifiedInterpreter}
              setExcludedInterpreters={setExcludedInterpreters}
              // language={methods.getValues("language_id")}
              language={watch("language_id")}
              initialExcludedStr={item?.appointment_details?.excluded_interpreters || ''}
            />
          )}
        </>

        {/* ERROR MESSAGES */}
        <>
          {Object.keys(errors)?.length > 0 && (
            <div className="row-primary align-center">
              <i class="fa-solid fa-circle-exclamation mt-2 error-color "></i>
              <ErrorMessage
                fontWeight={"bold"}
                text={"Please fill all mandatory fields"}
              />
            </div>
          )}

          {error?.errors?.billed?.map((error) => (
            <div style={{ marginBottom: 10 }}>
              <ErrorMessage text={error} />
            </div>
          ))}
          {error?.errors?.mrn_number?.map((error) => (
            <div style={{ marginBottom: 10 }}>
              <ErrorMessage text={error} />
            </div>
          ))}
          {error?.errors?.requestor_phone_num?.map((error) => (
            <div style={{ marginBottom: 10 }}>
              <ErrorMessage text={error} />
            </div>
          ))}
        </>

        <div className="Form-footer mt-3">
          <button
            type="button"
            className="Cancle_button"
            data-bs-dismiss={itsBootstrap ? "modal" : ""}
            onClick={handleClose}
          >
            Cancel
          </button>
          <SubmitButton
            btnText="Submit Request"
            buttonType="button"
            handleSubmit={handleSubmit(onSubmit, onError)}
            loader={loaderState}
          />
        </div>
      </form>
    </FormProvider>
  );
};

const JobLocationAndCoutrooms = ({ item, dispatch, control, errors, watch, setValue }) => {

  const { jobLocation } = useSelector(jobLocationSelector)
  // True only when the user actively picks a location, so we prefill the address from the
  // location's default ONLY then — never overwrite the job's saved address on initial load.
  const userSelectedLocationRef = useRef(false);

  //     useEffect(() => {
  //   if (jobLocation?.job_interpreter) {
  //     const excluded = jobLocation.job_interpreter.map((ji) => ({
  //       id: ji.interpreter.id,
  //       name: ji.interpreter.first_name + " " + (ji.interpreter.last_name || "")
  //     }));

  //     console.log("Default excluded interpreters:", excluded);
  //     // setExcludedInterpreters(excluded.map((ex) => ex.id));
  //   }
  // }, [jobLocation]);
  useEffect(() => {
    if (jobLocation) {
      console.log("Redux JobLocation:", jobLocation);
      console.log("Job Interpreters:", jobLocation?.job_interpreter);
      console.log("First Interpreter Record:", jobLocation?.job_interpreter?.[0]?.interpreter);
    }
  }, [jobLocation]);

  useEffect(() => {
    if (!jobLocation) return;
    setValue('type', jobLocation?.by_zoom ? 'VRI' : 'In Person')
    // Only prefill the address from the location's default when the user just selected a
    // location; on initial edit load keep the saved appointment address.
    if (userSelectedLocationRef.current) {
      setValue('location_address', jobLocation?.address || '')
      userSelectedLocationRef.current = false;
    }
  }, [jobLocation])

  // #region Job locations
  const [filterJobLocation, setFilterJobLocation] = useState({
    label: "",
    value: "",
  });

  useEffect(() => {
    const loc = item?.appointment_details?.job_location;
    if (loc?.name && loc?.id) {
      setFilterJobLocation({ label: loc.name, value: loc.id });
      setValue("job_location_id", loc.id);
      dispatch(fetchJobLocationById(loc.id, true));
    } else {
      setFilterJobLocation(null);
    }
  }, [item?.appointment_details?.job_location])

  const filteredJobLocationLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(
      fetchJobLocations(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue, status: "active" }, true, true)
    );
    return {
      options: result?.data?.map((val) => ({
        value: val.id,
        label: val.name,
      })),

      hasMore: page !== result?.last_page,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  return (
    <>
      <div className="row-primary">
        <h2>Job Location *</h2>
        <div className="w-100">
          <Controller
            name="job_location_id"
            control={control}
            rules={{
              required: "Please select a job location",
            }}
            render={({ field }) => (
              <div className="w-100">
                <AsyncPaginate
                  menuPlacement="auto"
                  debounceTimeout={500}
                  styles={{
                    ...selectCustomStyles,
                  }}
                  value={filterJobLocation}
                  loadOptions={filteredJobLocationLoadOptions}
                  isClearable
                  escapeClearsValue
                  onChange={(obj) => {
                    if (obj?.value) {
                      userSelectedLocationRef.current = true;
                      setFilterJobLocation(obj);
                      field.onChange(obj.value);
                      dispatch(fetchJobLocationById(obj.value, true));
                    } else {
                      setFilterJobLocation(null);
                      field.onChange(null);
                    }
                  }}
                  additional={{
                    page: item?.state?.id ? 2 : 1,
                  }}
                />
                {errors?.language_id?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage text={errors?.language_id?.message} />
                  </div>
                )}
              </div>
            )}
          />
        </div>
      </div>

      {watch("job_location_id") &&
        <div className="row-primary">
          <h2>Location Address</h2>
          <Controller
            name="location_address"
            control={control}
            render={({ field }) => (
              <textarea
                className="type_inp input-in"
                {...field}
                placeholder="location address"
                required
              ></textarea>
            )}
          />
        </div>
      }

      <div className="row-primary">
        <h2>Location Notes</h2>
        <Controller
          name="location_notes"
          control={control}
          render={({ field }) => (
            <textarea
              className="type_inp input-in"
              {...field}
              placeholder="location notes"
            ></textarea>
          )}
        />
      </div>

      {/* REQUESTER */}
      {/* <div className="row-primary">
        <h2>Type</h2>
        <div className="w-100">
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <input className="input-in type_inp" type="text" readOnly {...field} />
            )}
          />
        </div>
      </div> */}

    </>
  );
}

export const CheckinCheckout = ({ apptAssign }) => {
  const { control, setValue, watch } = useFormContext();
  const interpreterId = watch("interpreter_id");
  useEffect(() => {
    if (interpreterId) {
      setValue(
        "comments",
        commentsTypeOptions[commentsTypeOptions?.length - 1].value
      );
    } else {
      setValue("comments", "");
    }
  }, [interpreterId]);

  useEffect(() => {
    if (apptAssign?.checkin_date) {
      setValue("available_status", true);
    }
  }, []);
  return (
    <>
      <div className="row-primary">
        <Controller
          name="available_status"
          control={control}
          render={({ field }) => (
            <Checkbox
              title={"Add Check In / Check Out"}
              isChecked={!!apptAssign?.checkin_date && field?.value}
              handleCheck={field.onChange}
              value={field.value}
            />
          )}
        />
      </div>
    </>
  );
};

const DateTimeInputs = ({ isDuplicate, isNew }) => {
  const { control, formState, setValue, watch } = useFormContext();
  const outsideHours = isOutsideBusinessHours(watch("date"), watch("start_time"));
  return (
    <>
    <div className="row-primary row-r">
      <div className="date row-primary">
        <h2>Date*</h2>
        <Controller
          name="date"
          control={control}
          rules={{
            required: "Please select date",
          }}
          render={({ field }) => (
            <div>
              <div className="input-image">
                <input
                  id="date"
                  type="date"
                  name="date"
                  placeholder="Date"
                  required
                  {...field}
                  value={field.value ? field.value : ""}
                  onChange={(e) => {
                    const dateString = e.target.value
                      ? moment.utc(e.target.value).format("YYYY-MM-DD")
                      : "";
                    field.onChange(dateString);

                    if (watch("available_status")) {
                      if (isDuplicate || isNew) {
                        setValue("checkin_date", watch("date"));
                        setValue("checkout_date", watch("date"));
                      }
                    }
                  }}
                />
                <img
                  src="/images/celender.png"
                  alt="celender Icon"
                  className="custom-icon"
                />
              </div>
              {formState?.errors?.date?.message && (
                <div style={{ marginBottom: 10 }}>
                  <ErrorMessage text={formState?.errors?.date?.message} />
                </div>
              )}
            </div>
          )}
        />
      </div>

      <div className="time row-primary">
        <h2>Time*</h2>
        <Controller
          name="start_time"
          control={control}
          rules={{
            required: "Please select start time",
          }}
          render={({ field }) => (
            <div>
              <div className="input-image">
                <input
                  type="time"
                  name="start_time"
                  placeholder="hh:mm"
                  {...field}
                  value={moment(field.value, ["h:mm A"]).format("HH:mm")}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (watch("available_status")) {
                      if (isDuplicate || isNew) {
                        setValue("checkin_time", watch("start_time"));
                        const duraton = watch("duration");
                        const checkout_time = moment(e.target.value, ["HH:mm"])
                          .add(duraton, "minutes")
                          .format("HH:mm");
                        setValue("checkout_time", checkout_time);
                      }
                    }
                  }}
                  required
                />
                <img src="/images/clock.png" alt="Clock Icon" className="custom-icon" />
              </div>
              {formState?.errors?.start_time?.message && (
                <div style={{ marginBottom: 10 }}>
                  <ErrorMessage text={formState?.errors?.start_time?.message} />
                </div>
              )}
            </div>
          )}
        />
      </div>
    </div>
    {outsideHours && (
      <div
        className="softAlert"
        style={{
          background: "#fff4e5",
          borderLeft: "4px solid #ffb82a",
          padding: "12px 16px",
          margin: "12px 0",
          borderRadius: 4,
          color: "#7a4f01",
        }}
      >
        <span style={{ marginRight: 6 }}>⚠</span>
        This job is scheduled outside business hours (Mon–Fri 8:00am–4:30pm). You can still create it.
      </div>
    )}
    </>
  );
};

export const CheckinDateTimeInputs = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  useEffect(() => {
    if (watch("checkin_date") && !watch("checkin_time")) {
      setError("checkin_time", {
        message: "Checkin time required",
      });
    } else {
      clearErrors("checkin_time");
    }
  }, [watch("checkin_date"), watch("checkin_time")]);

  return (
    <div className="row-primary flex-column gap-0 mb-0">
      <h2 className="fst-italic">Add Check In</h2>
      <div className="row-primary row-r">
        <div className="date">
          <h2>Date*</h2>
          <Controller
            name="checkin_date"
            control={control}
            render={({ field }) => (
              <div style={{ width: "100%" }}>
                <div className="input-image">
                  <input
                    id="date"
                    type="date"
                    name="checkin_date"
                    placeholder="Date"
                    required
                    {...field}
                    value={
                      field.value
                        ? moment(field.value).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) => {
                      const dateString = e.target.value
                        ? moment.utc(e.target.value).format("YYYY-MM-DD")
                        : "";

                      field.onChange(dateString);
                      if (!dateString) {
                        setValue("checkin_time", "");
                      }
                    }}
                  />
                  <img
                    src="/images/celender.png"
                    alt="celender Icon"
                    className="custom-icon"
                  />
                </div>
              </div>
            )}
          />
        </div>

        <div className="time">
          <h2>Time*</h2>
          <Controller
            name="checkin_time"
            control={control}
            render={({ field }) => (
              <div style={{ width: "100%" }}>
                <div className="input-image">
                  <input
                    type="time"
                    name="checkin_time"
                    placeholder="hh:mm"
                    {...field}
                    required
                  />
                  <img
                    src="/images/clock.png"
                    alt="Clock Icon"
                    className="custom-icon"
                  />
                </div>

                {errors?.checkin_time?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage text={errors?.checkin_time?.message} />
                  </div>
                )}
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const CheckoutDateTimeInputs = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  useEffect(() => {
    if (watch("checkout_date") && !watch("checkout_time")) {
      setError("checkout_time", {
        message: "Checkout time required",
      });
    } else {
      clearErrors("checkout_time");
    }
  }, [watch("checkout_date"), watch("checkout_time")]);

  return (
    <div className="row-primary flex-column gap-0 mb-0">
      <h2 className="fst-italic">Add Check Out</h2>
      <div className="row-primary row-r">
        <div className="date">
          <h2>Date</h2>
          <Controller
            name="checkout_date"
            control={control}
            render={({ field }) => (
              <div style={{ width: "100%" }}>
                <div className="input-image">
                  <input
                    id="date"
                    type="date"
                    name="checkout_date"
                    placeholder="Date"
                    required
                    {...field}
                    value={
                      field.value
                        ? moment(field.value).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        const dateString = moment
                          .utc(e.target.value)
                          .format("YYYY-MM-DD");
                        field.onChange(dateString);

                        if (!dateString) {
                          setValue("checkout_time", "");
                        }
                      } else {
                        field.onChange("");
                        setValue("checkout_time", "");
                      }
                    }}
                  />
                  <img
                    src="/images/celender.png"
                    alt="celender Icon"
                    className="custom-icon"
                  />
                </div>
              </div>
            )}
          />
        </div>

        <div className="time">
          <h2>Time</h2>
          <Controller
            name="checkout_time"
            control={control}
            render={({ field }) => (
              <div style={{ width: "100%" }}>
                <div className="input-image">
                  <input
                    type="time"
                    name="checkout_time"
                    placeholder="hh:mm"
                    {...field}
                    required
                  />
                  <img
                    src="/images/clock.png"
                    alt="Clock Icon"
                    className="custom-icon"
                  />
                </div>

                {errors?.checkout_time?.message && (
                  <div style={{ margoutBottom: 10 }}>
                    <ErrorMessage text={errors?.checkout_time?.message} />
                  </div>
                )}
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const CommentsAndNotes = () => {
  const { control, setValue, watch } = useFormContext();

  useEffect(() => {
    if (watch("interpreter_id"))
      setValue(
        "comments",
        commentsTypeOptions[commentsTypeOptions?.length - 1].value
      );
    else setValue("comments", "");
  }, [watch("interpreter_id")]);

  return (
    <>
      <div className="row-primary">
        <h2>Comments</h2>
        <Controller
          name="comments"
          control={control}
          render={({ field }) => (
            <Select
              name="comments"
              options={commentsTypeOptions || []}
              {...field}
              value={
                field.value
                  ? commentsTypeOptions?.find((opt) => field.value == opt.value)
                  : ""
              }
              onChange={field.onChange}
            />
          )}
        />
        {/* <p className="error">{field.error?.message}</p> */}
      </div>
      <div className="row-primary">
        <h2>Notes</h2>

        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <textarea
              className="input-in type_inp"
              type="text"
              name="notes"
              placeholder="Enter notes and additional comments"
              {...control}
              {...field}
            />
          )}
        />
      </div>
    </>
  );
};

export const DurationRange = ({ control, defaultDuration }) => {
  return (
    <div className="row-primary mt-5 mtSm_0">
      <h2>Job Duration</h2>
      <div className="w-100">
        <label for="customRange1" className="form-label"></label>
        <Controller
          name="duration"
          control={control}
          render={({ field }) => (
            <Slider
              min={0}
              max={500}
              defaultValue={defaultDuration}
              step={5}
              aria-label="Small"
              valueLabelDisplay="on"
              valueLabelFormat={(value) => {
                if (!value) {
                  return "0 min";
                }
                const hours = Math.floor(value / 60);
                const minutes = value % 60;
                if (minutes === 0) {
                  return `${hours}hour${hours > 1 ? "s" : ""}`;
                }
                if (hours === 0) {
                  return `${minutes} min`;
                }
                return `${hours}hr ${minutes.toString().padStart(2, "0")}min`;
              }}
              sx={{
                color: "#6F8DFF",
                "& .MuiSlider-valueLabel": {
                  backgroundColor: "#6F8DFF",
                  color: "#FFFFFF",
                },
              }}
              value={field.value}
              onChange={(event, value) => field.onChange(value)}
            />
          )}
        />
      </div>
    </div>
  );
};

const InterpreterExclusion = ({ dispatch, certified, nonCertified, watch, setExcludedInterpreters, language, initialExcludedStr }) => {
  const { interpreter, listLoading } = useSelector(interpreterSelector);
  const [available, setAvailable] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedExcluded, setSelectedExcluded] = useState([]);
  const { jobLocation } = useSelector(jobLocationSelector)
  const selectedDate = watch("date");
  const initializedRef = useRef(false);

  // Stable array derived from the primitive string — only recomputes when the string changes
  const initialExcludedIds = useMemo(
    () => initialExcludedStr ? String(initialExcludedStr).split(',').map(Number).filter(Boolean) : [],
    [initialExcludedStr]
  );

  // Reset initialization flag when a different appointment is opened
  useEffect(() => {
    initializedRef.current = false;
  }, [initialExcludedStr]);

  // Pre-populate excluded list from saved appointment data once interpreter list is ready
  useEffect(() => {
    if (initializedRef.current) return;
    if (!interpreter?.length || !initialExcludedIds.length) return;

    const preExcluded = interpreter
      .filter(i => initialExcludedIds.includes(i.id))
      .map(i => ({ id: i.id, name: interpreterName(i) }));

    if (preExcluded.length) {
      setExcluded(preExcluded);
      setExcludedInterpreters(preExcluded.map(i => i.id));
    }
    initializedRef.current = true;
  }, [interpreter, initialExcludedStr]);

  useEffect(() => {
    if (!language) {
      setAvailable([]);
      setSelectedAvailable([]);
      return;
    }

    if (!interpreter) return;

    let filtered = interpreter;

    filtered = filtered.filter(
      (item) => !excluded.some((ex) => ex.id === item.id)
    );

    if (selectedDate) {
      filtered = filtered.filter((item) => {
        if (!item.unavailable_dates || item.unavailable_dates.length === 0)
          return true;

        const isUnavailable = item.unavailable_dates.some((ud) => {
          const start = moment(ud.start_date);
          const end = moment(ud.end_date);
          const selected = moment(selectedDate);
          return selected.isBetween(start, end, "day", "[]");
        });

        return !isUnavailable;
      });
    }
    setAvailable(
      filtered.map((item) => ({
        id: item.id,
        name: interpreterName(item),
      }))
    );
  }, [interpreter, excluded, selectedDate, language]);


  // useEffect(
  //   () => {
  //     if (jobLocation?.job_interpreter) {
  //     const newexcluded = jobLocation.job_interpreter.filter(
  //       (ji) => ji?.interpreter?.new_rates?.some(rate => rate.language_id == language)
  //     ).map((ji) => ({
  //       id: ji.interpreter.id,
  //       name: ji.interpreter.first_name + " " + (ji.interpreter.last_name || ""),
  //     }));

  //   const newExcluded = [
  //     ...newexcluded

  //   ];
  //   setExcluded(newexcluded);
  //   setExcludedInterpreters(newexcluded.map((item) => item.id));
  // }

  //   },[jobLocation?.job_interpreter,language]
  // )
useEffect(() => {
  if (!language) {
    setExcluded([]);
    setSelectedExcluded([]);
    setExcludedInterpreters([]);
    return;
  }

  // On edit, the job's saved excluded_interpreters (handled by the effect above) is the
  // source of truth — don't let the job-location-derived list override it.
  if (initialExcludedStr) return;

  if (!jobLocation?.job_interpreter) return;

  const selected = moment(selectedDate);

  let newExcluded = jobLocation.job_interpreter
    // ✅ Step 1: Filter by selected language
    .filter((ji) =>
      ji?.interpreter?.new_rates?.some(
        (rate) => rate?.language_id == language
      )
    )

    .filter((ji) => {
      const rate = ji?.interpreter?.new_rates?.[0];
      const certId = rate?.certification_id;

      if (certified && nonCertified) return true;
      if (certified && !nonCertified) return certId == 3;
      if (!certified && nonCertified) return certId == 1;
      return false;
    })

    .filter((ji) => {
      const unavailable = ji?.interpreter?.unavailable_dates || [];
      if (unavailable.length === 0) return true;

      const isUnavailable = unavailable.some((ud) => {
        const start = moment(ud?.start_date);
        const end = moment(ud?.end_date);
        return selected.isBetween(start, end, "day", "[]");
      });

      return !isUnavailable;
    })

    .map((ji) => ({
      id: ji?.interpreter?.id,
      name: interpreterName(ji?.interpreter),
    }));

  setExcluded(newExcluded);
  setExcludedInterpreters(newExcluded.map((item) => item.id));
}, [jobLocation?.job_interpreter, language, selectedDate, certified, nonCertified]);


  

  // name: ji.interpreter.first_name + " " + (ji.interpreter.last_name || ""),
  const selectedLanguage = watch("language_id");

  useEffect(() => {
    let showFilter = 'none';

    if (certified == 1 && nonCertified != 1) {
      showFilter = "certified";
    } else if (nonCertified == 1 && certified != 1) {
      showFilter = "nonCertified";
    } else if (certified == 1 && nonCertified == 1) {
      showFilter = "all";
    }

    if (selectedLanguage) {
      dispatch(
        fetchInterpreters(1, {
          listType: "all",
          showFilter: showFilter,
          language_id: selectedLanguage,
          status: "active",
        }, true)
      );
    } else {
      setAvailable([]);
      setExcluded([]);
      setSelectedAvailable([]);
      setSelectedExcluded([]);
      setExcludedInterpreters([]);
    }
  }, [certified, nonCertified, selectedLanguage, dispatch]);

  // useEffect(() => {
  //   if (interpreter) {
  //     // filter out already excluded interpreters
  //     const filtered = interpreter.filter(
  //       (item) => !excluded.some((ex) => ex.id === item.id)
  //     );

  //     setAvailable(
  //       filtered.map((item) => ({
  //         id: item.id,
  //         name: item.first_name + " " + (item.last_name || ""),
  //       }))
  //     );
  //   }
  // }, [interpreter, excluded]);

  // Move to excluded
  const moveRight = () => {
    const newExcluded = [
      ...excluded,
      ...available.filter((item) =>
        selectedAvailable.includes(item.id.toString())
      ),
    ];
    setExcluded(newExcluded);
    setExcludedInterpreters(newExcluded.map((item) => item.id));
    setAvailable(
      available.filter((item) => !selectedAvailable.includes(item.id.toString()))
    );
    setSelectedAvailable([]);
  };

  // Move back to available
  const moveLeft = () => {
    const movedBack = excluded.filter((item) =>
      selectedExcluded.includes(item.id.toString())
    );
    const newAvailable = [...available, ...movedBack];
    const newExcluded = excluded.filter(
      (item) => !selectedExcluded.includes(item.id.toString())
    );

    setAvailable(newAvailable);
    setExcluded(newExcluded);
    setExcludedInterpreters(newExcluded.map((item) => item.id));
    setSelectedExcluded([]);
  };



  return (
    <>
      <h2 className="exclusion_title">Interpreter Exclusions</h2>
    <div className="exclusion_wrapper">
      <div className="exclusion panel_wrapper">
        {/* Available Interpreters */}
        <div className="exclusion_panel">
          <p>All Available Interpreters:</p>
          <select
            multiple
            value={selectedAvailable}
            onChange={(e) =>
              setSelectedAvailable(Array.from(e.target.selectedOptions, (option) => option.value))
            }
          >
            {listLoading ?
              Array.from({ length: 3 }, (_, index) => index).map((item) => (
                <Skeleton variant="rectangular" width={210} height={30} className="mb-2" />
              )) :
              available.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </div>

        {/* Buttons */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            justifyContent: "center",
          }}
        >
          <SubmitButton
            buttonType="button"
            onClick={moveRight}
            btnText={<ChevronsRight />}
          />
          <SubmitButton
            buttonType="button"
            onClick={moveLeft}
            btnText={<ChevronsLeft />}
          />
        </div>

        {/* Excluded Interpreters */}
        <div className="exclusion_panel">
          <p>Excluded Interpreters:</p>
          <select
            multiple
            value={selectedExcluded}
            onChange={(e) =>
              setSelectedExcluded(Array.from(e.target.selectedOptions, (option) => option.value))
            }
          >
            {listLoading ?
              Array.from({ length: 3 }, (_, index) => index).map((item) => (
                <Skeleton variant="rectangular" width={210} height={30} className="mb-2" />
              )) :
              excluded.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </div>
      </div>
    </div>
    </>
  );
};


export default ScheduleForm;
